import React, { useMemo } from 'react';
import { Source, Layer } from 'react-map-gl';
import { forward as mgrsForward } from 'mgrs';
import {
  bearingDeg,
  concentricRadiiKm,
  destination,
  formatAreaKm2,
  formatBearing,
  formatDistanceKm,
  geodesicCircle,
  haversineKm,
  pathLengthKm,
  polygonAreaKm2,
  type MeasureLngLat,
  type MeasureMode,
} from '../../services/mapMeasure';

const midpoint = (a: MeasureLngLat, b: MeasureLngLat): MeasureLngLat =>
  [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

const centroid = (pts: MeasureLngLat[]): MeasureLngLat => {
  const n = pts.length;
  return [
    pts.reduce((s, p) => s + p[0], 0) / n,
    pts.reduce((s, p) => s + p[1], 0) / n,
  ];
};

const labelFeature = (coords: MeasureLngLat, label: string, kind: string): GeoJSON.Feature => ({
  type: 'Feature',
  properties: { label, kind },
  geometry: { type: 'Point', coordinates: coords },
});

const LINE = '#ff5722';
const FILL = 'rgba(255, 87, 34, 0.18)';
const RING = '#ffc107';

export interface MapMeasureLayersProps {
  mode: MeasureMode;
  points: MeasureLngLat[];
  ringSpacingM: number;
  ringCount: number;
  /** 0–1. Fades range-ring fill and outlines (default 0.85). */
  ringOpacity?: number;
}

const MapMeasureLayers: React.FC<MapMeasureLayersProps> = ({
  mode,
  points,
  ringSpacingM,
  ringCount,
  ringOpacity = 0.85,
}) => {
  const path = useMemo(() => {
    if (mode !== 'distance' || points.length < 2) return null;
    return {
      type: 'Feature' as const,
      properties: {},
      geometry: { type: 'LineString' as const, coordinates: points },
    };
  }, [mode, points]);

  const spoke = useMemo(() => {
    if ((mode !== 'radius' && mode !== 'bearing') || points.length < 2) return null;
    return {
      type: 'Feature' as const,
      properties: {},
      geometry: { type: 'LineString' as const, coordinates: [points[0], points[1]] },
    };
  }, [mode, points]);

  const radiusCircle = useMemo(() => {
    if (mode !== 'radius' || points.length < 2) return null;
    const km = haversineKm(points[0], points[1]);
    if (!(km > 0)) return null;
    return {
      type: 'Feature' as const,
      properties: {},
      geometry: { type: 'Polygon' as const, coordinates: [geodesicCircle(points[0], km)] },
    };
  }, [mode, points]);

  const area = useMemo(() => {
    if (mode !== 'area' || points.length < 2) return null;
    const ring = points.length >= 3 ? [...points, points[0]] : points;
    const geometry = points.length >= 3
      ? { type: 'Polygon' as const, coordinates: [ring] }
      : { type: 'LineString' as const, coordinates: ring };
    return { type: 'Feature' as const, properties: {}, geometry };
  }, [mode, points]);

  const rings = useMemo(() => {
    if (mode !== 'rings' || points.length < 1) {
      return { type: 'FeatureCollection' as const, features: [] as GeoJSON.Feature[] };
    }
    const center = points[0];
    return {
      type: 'FeatureCollection' as const,
      features: concentricRadiiKm(ringSpacingM, ringCount).map((km) => ({
        type: 'Feature' as const,
        properties: { label: formatDistanceKm(km) },
        geometry: { type: 'Polygon' as const, coordinates: [geodesicCircle(center, km)] },
      })),
    };
  }, [mode, points, ringSpacingM, ringCount]);

  const ringLabels = useMemo(() => {
    if (mode !== 'rings' || points.length < 1) {
      return { type: 'FeatureCollection' as const, features: [] as GeoJSON.Feature[] };
    }
    const center = points[0];
    return {
      type: 'FeatureCollection' as const,
      features: concentricRadiiKm(ringSpacingM, ringCount).map((km) => ({
        type: 'Feature' as const,
        properties: { label: formatDistanceKm(km) },
        geometry: { type: 'Point' as const, coordinates: destination(center, 0, km) },
      })),
    };
  }, [mode, points, ringSpacingM, ringCount]);

  // Result labels drawn on the map itself, so the figure still reads when the
  // card is minimized. Segment labels sit on each leg; the total / area /
  // azimuth label sits at the end point, centroid, or radius midpoint.
  const resultLabels = useMemo(() => {
    const features: GeoJSON.Feature[] = [];
    if (mode === 'distance' && points.length >= 2) {
      for (let i = 1; i < points.length; i++) {
        const km = haversineKm(points[i - 1], points[i]);
        if (km > 0) features.push(labelFeature(midpoint(points[i - 1], points[i]), formatDistanceKm(km), 'segment'));
      }
      if (points.length > 2) {
        features.push(labelFeature(points[points.length - 1], `Total ${formatDistanceKm(pathLengthKm(points))}`, 'total'));
      }
    } else if (mode === 'area' && points.length >= 3) {
      const km2 = polygonAreaKm2(points);
      if (km2 > 0) features.push(labelFeature(centroid(points), formatAreaKm2(km2), 'total'));
    } else if (mode === 'radius' && points.length >= 2) {
      const km = haversineKm(points[0], points[1]);
      if (km > 0) features.push(labelFeature(midpoint(points[0], points[1]), `r = ${formatDistanceKm(km)}`, 'total'));
    } else if (mode === 'bearing' && points.length >= 2) {
      const km = haversineKm(points[0], points[1]);
      const az = bearingDeg(points[0], points[1]);
      features.push(labelFeature(midpoint(points[0], points[1]), formatDistanceKm(km), 'segment'));
      features.push(labelFeature(points[1], formatBearing(az), 'total'));
    } else if (mode === 'coords' && points.length >= 1) {
      const [lng, lat] = points[0];
      let mgrs = '';
      try { mgrs = mgrsForward([lng, lat], 5); } catch { mgrs = ''; }
      const text = mgrs ? `${lat.toFixed(5)}, ${lng.toFixed(5)}\n${mgrs}` : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      features.push(labelFeature(points[0], text, 'total'));
    }
    return { type: 'FeatureCollection' as const, features };
  }, [mode, points]);

  return (
    <>
      {area && area.geometry.type === 'Polygon' && (
        <Source id="measure-area" type="geojson" data={area}>
          <Layer id="measure-area-fill" type="fill" paint={{ 'fill-color': FILL }} />
          <Layer
            id="measure-area-outline"
            type="line"
            paint={{ 'line-color': LINE, 'line-width': 2 }}
          />
        </Source>
      )}
      {area && area.geometry.type === 'LineString' && (
        <Source id="measure-area-open" type="geojson" data={area}>
          <Layer
            id="measure-area-open-line"
            type="line"
            paint={{ 'line-color': LINE, 'line-width': 2, 'line-dasharray': [2, 2] }}
          />
        </Source>
      )}
      {path && (
        <Source id="measure-path" type="geojson" data={path}>
          <Layer
            id="measure-path-line"
            type="line"
            layout={{ 'line-join': 'round' }}
            paint={{ 'line-color': LINE, 'line-width': 3, 'line-dasharray': [2, 2] }}
          />
        </Source>
      )}
      {radiusCircle && (
        <Source id="measure-radius" type="geojson" data={radiusCircle}>
          <Layer id="measure-radius-fill" type="fill" paint={{ 'fill-color': 'rgba(255, 87, 34, 0.12)' }} />
          <Layer
            id="measure-radius-line"
            type="line"
            paint={{ 'line-color': LINE, 'line-width': 2, 'line-dasharray': [2, 2] }}
          />
        </Source>
      )}
      {spoke && (
        <Source id="measure-spoke" type="geojson" data={spoke}>
          <Layer
            id="measure-spoke-line"
            type="line"
            paint={{ 'line-color': LINE, 'line-width': 2.5, 'line-dasharray': [2, 2] }}
          />
        </Source>
      )}
      {rings.features.length > 0 && (
        <Source id="measure-rings" type="geojson" data={rings}>
          <Layer
            id="measure-rings-fill"
            type="fill"
            paint={{ 'fill-color': '#ffc107', 'fill-opacity': Math.max(0.01, ringOpacity * 0.12) }}
          />
          <Layer
            id="measure-rings-line"
            type="line"
            paint={{ 'line-color': RING, 'line-width': 1.5, 'line-opacity': ringOpacity }}
          />
        </Source>
      )}
      {ringLabels.features.length > 0 && (
        <Source id="measure-ring-labels" type="geojson" data={ringLabels}>
          <Layer
            id="measure-ring-labels-text"
            type="symbol"
            layout={{
              'text-field': ['get', 'label'],
              'text-size': 11,
              'text-anchor': 'bottom',
              'text-offset': [0, -0.35],
              'text-allow-overlap': true,
            }}
            paint={{
              'text-color': '#ffe082',
              'text-opacity': Math.max(0.35, ringOpacity),
              'text-halo-color': '#000',
              'text-halo-width': 1.2,
            }}
          />
        </Source>
      )}
      {resultLabels.features.length > 0 && (
        <Source id="measure-result-labels" type="geojson" data={resultLabels}>
          <Layer
            id="measure-result-labels-text"
            type="symbol"
            layout={{
              'text-field': ['get', 'label'],
              'text-size': ['case', ['==', ['get', 'kind'], 'total'], 12, 11],
              'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
              'text-anchor': 'bottom',
              'text-offset': [0, -0.9],
              'text-allow-overlap': true,
              'text-ignore-placement': true,
              'text-line-height': 1.2,
            }}
            paint={{
              'text-color': ['case', ['==', ['get', 'kind'], 'total'], '#ffffff', '#ffccbc'],
              'text-halo-color': 'rgba(0,0,0,0.85)',
              'text-halo-width': 1.4,
            }}
          />
        </Source>
      )}
    </>
  );
};

export default MapMeasureLayers;
